import type { MusicVideoClipSource } from 'fym-shared/src/music24/musicVideoDoc';
import type {
  BankFootageItem,
  FootageAiStyle,
  FootageFailCode,
  FootagePeople,
  FootageSource,
  FootageStatus,
  SerializedFootageClip,
} from 'fym-shared/src/music24/musicVideoApi';

/** Where a clip of the panels comes from: the shared bank, or one of the three own kinds. */
export type FootageOrigin = 'bank' | FootageSource;

/** One clip as the panels, the timeline and the preview show it. */
export interface FootageView {
  /** `bank:<uuid>` or `footage:<uuid>`. */
  key: string;
  source: MusicVideoClipSource;
  origin: FootageOrigin;
  label: string;
  posterUrl: string | null;
  previewUrl: string | null;
  durationSec: number | null;
  width: number | null;
  height: number | null;
  /** A bank clip is always ready; an own clip only with the status `ready`. */
  status: FootageStatus;
  failCode: FootageFailCode | null;
  prompt: string | null;
  /** The look and the people of an AI clip ("Make a variation"); null for the other clips. */
  style: FootageAiStyle | null;
  people: FootagePeople | null;
  catalogItemUuid: string | null;
  createdAt: string | null;
}

export function footageKey(source: MusicVideoClipSource): string {
  return `${source.kind}:${source.clipUuid}`;
}

export function bankFootageView(item: BankFootageItem): FootageView {
  const source: MusicVideoClipSource = { kind: 'bank', clipUuid: item.uuid };
  return {
    key: footageKey(source),
    source,
    origin: 'bank',
    label: item.label,
    posterUrl: item.posterUrl,
    previewUrl: item.previewUrl,
    durationSec: item.durationSec,
    width: null,
    height: null,
    status: 'ready',
    failCode: null,
    prompt: null,
    style: null,
    people: null,
    catalogItemUuid: null,
    createdAt: null,
  };
}

export function ownFootageView(clip: SerializedFootageClip): FootageView {
  const source: MusicVideoClipSource = { kind: 'footage', clipUuid: clip.uuid };
  return {
    key: footageKey(source),
    source,
    origin: clip.source,
    label: clip.name,
    posterUrl: clip.posterUrl,
    previewUrl: clip.previewUrl,
    durationSec: clip.durationSec,
    width: clip.width,
    height: clip.height,
    status: clip.status,
    failCode: clip.failCode,
    prompt: clip.prompt,
    style: clip.style,
    people: clip.people,
    catalogItemUuid: clip.catalogItemUuid,
    createdAt: clip.createdAt,
  };
}

/** True for a clip that may go on the timeline (a ready clip with a file). */
export function isUsableFootage(view: FootageView): boolean {
  return view.status === 'ready' && view.previewUrl !== null;
}

/** True for a clip taller than wide (design c3: "Fit the 16:9 frame" with the crop picker). */
export function isVerticalFootage(view: Pick<FootageView, 'width' | 'height'>): boolean {
  return view.width !== null && view.height !== null && view.height > view.width;
}

/** How many times each clip is in the cut ("In use · 2×", design b1). */
export function usageCounts(clips: ReadonlyArray<{ source: MusicVideoClipSource }>): Map<string, number> {
  const counts = new Map<string, number>();
  for (const clip of clips) {
    const key = footageKey(clip.source);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/**
 * "Replace with a similar clip" (design b1): the first candidate (the director's picks for the
 * section) that is not the clip itself, the one the cut uses least. Null when none is left. Pure.
 */
export function pickSimilarFootage(
  candidates: readonly FootageView[],
  current: MusicVideoClipSource,
  uses: ReadonlyMap<string, number>
): FootageView | null {
  const currentKey = footageKey(current);
  let best: FootageView | null = null;
  for (const view of candidates) {
    if (view.key !== currentKey && isUsableFootage(view)) {
      if (best === null || (uses.get(view.key) ?? 0) < (uses.get(best.key) ?? 0)) {
        best = view;
      }
    }
  }
  return best;
}
